export type RateType = "fixed" | "variable";
export type LoanFor = "personal" | "business";
export type VehicleCondition = "new" | "demo" | "used";

export interface InputParams {
  rateType: RateType;
  loanFor: LoanFor;
  isPropertyOwner: boolean;
  vehicleCondition: VehicleCondition;
  modelYears: number;
}

const rentalRateTable: Record<
  RateType,
  Record<
    LoanFor,
    Record<
      "PropertyOwner" | "NonPropertyOwner",
      Record<VehicleCondition, Record<string, number>>
    >
  >
> = {
  fixed: {
    personal: {
      PropertyOwner: {
        new: { "": 9.89 },
        demo: { "": 9.89 },
        used: {
          "<3": 10.59,
          "4-5": 10.89,
          "6-7": 12.09,
          "8-12": 15.79,
        },
      },
      NonPropertyOwner: {
        new: { "": 11.39 },
        demo: { "": 11.39 },
        used: {
          "<3": 12.09,
          "4-5": 12.39,
          "6-7": 13.59,
          "8-12": 17.29,
        },
      },
    },
    business: {
      PropertyOwner: {
        new: { "": 10.39 },
        demo: { "": 10.39 },
        used: {
          "<3": 11.09,
          "4-5": 11.39,
          "6-7": 12.59,
          "8-12": 16.29,
        },
      },
      NonPropertyOwner: {
        new: { "": 11.89 },
        demo: { "": 11.89 },
        used: {
          "<3": 12.59,
          "4-5": 12.89,
          "6-7": 14.09,
          "8-12": 17.79,
        },
      },
    },
  },
  variable: {
    personal: {
      PropertyOwner: {
        new: { "": 9.14 },
        demo: { "": 9.14 },
        used: {
          "<3": 10.64,
          "4-5": 10.94,
          "6-7": 11.54,
          "8-12": 15.44,
        },
      },
      NonPropertyOwner: {
        new: { "": 10.64 },
        demo: { "": 10.64 },
        used: {
          "<3": 12.14,
          "4-5": 12.44,
          "6-7": 13.04,
          "8-12": 16.94,
        },
      },
    },
    business: {
      PropertyOwner: {
        new: { "": 9.14 },
        demo: { "": 9.14 },
        used: {
          "<3": 10.64,
          "4-5": 10.94,
          "6-7": 11.54,
          "8-12": 15.44,
        },
      },
      NonPropertyOwner: {
        new: { "": 10.64 },
        demo: { "": 10.64 },
        used: {
          "<3": 12.14,
          "4-5": 12.44,
          "6-7": 13.04,
          "8-12": 16.94,
        },
      },
    },
  },
};

function getVehicleAgeRange(years: number): string {
  if (years <= 0) return "";
  if (years < 3) return "<3";
  if (years <= 5) return "4-5";
  if (years <= 7) return "6-7";
  return "8-12";
}

export function getRentalRate(params: InputParams): number | undefined {
  const { rateType, loanFor, isPropertyOwner, vehicleCondition, modelYears } =
    params;

  const ownershipKey = isPropertyOwner ? "PropertyOwner" : "NonPropertyOwner";

  // If modelYears > 0, force vehicleCondition to "used"
  const effectiveCondition = modelYears > 0 ? "used" : vehicleCondition;

  const rateData =
    rentalRateTable[rateType]?.[loanFor]?.[ownershipKey]?.[effectiveCondition];

  if (!rateData) return undefined;

  // For "new" and "demo", return the default rate
  if (effectiveCondition === "new" || effectiveCondition === "demo") {
    return rateData[""];
  }

  // For "used", find appropriate age range
  const ageRange = getVehicleAgeRange(modelYears);
  // return rateData[ageRange];
  return rateData[ageRange] ?? rateData["<3"];
}
